import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import {
  DeleteDevicesApiResponse,
  DeleteDevicesResult,
  DeviceDocumentFirestoreRequest,
} from '../../types';

export const DEVICES_QUERY_KEY = 'pods';

type CreateParams = { data: DeviceDocumentFirestoreRequest };
type UpdateParams = { podId: string; data: DeviceDocumentFirestoreRequest };
/** 1..N id — usunięcie pojedyncze to tablica jednoelementowa. */
type DeleteParams = { ids: string[] };

// Cały CRUD podów idzie przez backend (codebase `admin`, admin SDK + superadmin token).
// Tłumaczenie tekstu powitalnego dzieje się w TLE (trigger v3-onDeviceCreated/Updated),
// więc endpointy wracają od razu, a lista pokazuje `translationStatus: pending`.
export const useCreateDevice = (
  options?: UseMutationOptions<void, AxiosError, CreateParams>,
): UseMutationResult<void, AxiosError, CreateParams> => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, CreateParams>({
    mutationFn: async ({ data }) => {
      await axiosInstance.post('/v3-createDevice', data);
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [DEVICES_QUERY_KEY] });
      options?.onSuccess?.(...args);
    },
  });
};

// Nazwa endpointu ma sufiks `Admin`, bo `v3-updateDevice` należy do panelu hotelu
// (codebase `content`) i autoryzuje członka organizacji — superadmin dostałby tam 403.
export const useUpdateDevice = (
  options?: UseMutationOptions<void, AxiosError, UpdateParams>,
): UseMutationResult<void, AxiosError, UpdateParams> => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, UpdateParams>({
    mutationFn: async ({ podId, data }) => {
      await axiosInstance.put('/v3-updateDeviceAdmin', { podId, data });
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [DEVICES_QUERY_KEY] });
      options?.onSuccess?.(...args);
    },
  });
};

// Single i bulk tym samym endpointem — body przy DELETE leci przez `config.data`.
export const useDeleteDevices = (
  options?: UseMutationOptions<DeleteDevicesResult, AxiosError, DeleteParams>,
): UseMutationResult<DeleteDevicesResult, AxiosError, DeleteParams> => {
  const queryClient = useQueryClient();

  return useMutation<DeleteDevicesResult, AxiosError, DeleteParams>({
    mutationFn: async ({ ids }) => {
      const res = await axiosInstance.delete<DeleteDevicesApiResponse>('/v3-deleteDevices', {
        data: { ids },
      });
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [DEVICES_QUERY_KEY] });
      options?.onSuccess?.(...args);
    },
  });
};
